// Billing → Payment collection (platform operator console). KaziPeople's OWN
// direct Vodacom M-Pesa collection: customers pay subscription invoices straight
// into the KaziPeople business shortcode. Credentials are versioned and write-only
// (the API never returns the key); changing / testing them is platform owner/admin
// only, and every action lands in the append-only audit list below.
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Descriptions, Empty, Form, Input, Row, Select, Space, Table, Tag, Typography, message } from 'antd';
import { ApiOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { PageHeader, useConfirmWithReason } from '../../components/console';
import { fmtDateTime } from '../../lib/format';
import * as api from '../../services/collectionConfigService';
import { auditLabel, collectionHeadline } from './paymentCollectionLogic';

const { Text } = Typography;

export const PaymentCollectionPage: React.FC = () => {
  const [status, setStatus] = useState<api.CollectionStatus | null>(null);
  const [audit, setAudit] = useState<api.CollectionAuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState<string | null>(null);
  const [auditFailed, setAuditFailed] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [form] = Form.useForm<api.SaveCredentialsBody>();
  const { confirm, dialog } = useConfirmWithReason();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(null);
    try {
      const s = await api.getCollectionStatus();
      setStatus(s);
      form.setFieldsValue({ environment: s.environment ?? 'sandbox', serviceProviderCode: s.service_provider_code ?? undefined, itemsDesc: s.items_desc ?? undefined });
    } catch (e) {
      setLoadFailed(api.apiErrorMessage(e, 'Could not load collection status.'));
    } finally {
      setLoading(false);
    }
    try {
      setAudit(await api.getCollectionAudit());
      setAuditFailed(null);
    } catch (e) {
      setAuditFailed(api.apiErrorMessage(e, 'Could not load the audit list.'));
    }
  }, [form]);

  useEffect(() => { load(); }, [load]);

  const head = collectionHeadline(status, !!loadFailed);

  const save = async () => {
    const v = await form.validateFields();
    const envChanged = !!status?.environment && v.environment !== status.environment;
    let reason: string | null = '';
    if (v.environment === 'live') {
      reason = await confirm({
        title: 'Save LIVE M-Pesa credentials?',
        message: 'Live credentials collect real money into the KaziPeople M-Pesa account once tested and enabled. Saving disables collection until you test and enable again.',
        danger: true,
        confirmPhrase: 'LIVE',
        reasonRequired: true,
        okText: 'Save live credentials',
      });
      if (reason === null) return;
    } else if ((status && status.source !== 'none') || envChanged) {
      reason = await confirm({
        title: envChanged ? 'Switch M-Pesa environment?' : 'Rotate M-Pesa credentials?',
        message:
          (status?.source === 'env' ? 'Saved credentials replace the environment fallback. ' : '') +
          'This creates a new credential version and DISABLES collection until you run a connection test and enable it again. Payments already in flight keep using the version they were sent with.',
        reasonRequired: false,
        okText: 'Save',
      });
      if (reason === null) return;
    }
    setSaving(true);
    try {
      const body: api.SaveCredentialsBody = {
        ...v,
        apiKey: v.apiKey?.trim() || undefined,
        publicKey: v.publicKey?.trim() || undefined,
        confirmLive: v.environment === 'live' ? 'LIVE' : undefined,
        reason: reason || undefined,
      };
      const s = envChanged ? await api.switchEnvironment(body) : await api.saveCredentials(body);
      setStatus(s);
      form.setFieldsValue({ apiKey: undefined, publicKey: undefined });
      message.success(`Saved as credential v${s.active_version}. Run a connection test, then enable.`);
      load();
    } catch (e) {
      message.error(api.apiErrorMessage(e, 'Could not save the credentials.'));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const r = await api.testConnection();
      setStatus(r.status);
      if (r.ok) message.success(r.message);
      else message.error(r.message);
      load();
    } catch (e) {
      message.error(api.apiErrorMessage(e, 'Connection test could not run.'));
    } finally {
      setTesting(false);
    }
  };

  const toggle = async (enabled: boolean) => {
    const reason = await confirm({
      title: enabled ? 'Enable direct M-Pesa collection?' : 'Disable direct M-Pesa collection?',
      message: enabled
        ? `Customers will be able to pay invoices with M-Pesa into shortcode ${status?.service_provider_code ?? ''} (${status?.environment ?? ''}).`
        : 'Customers will see "Online payment is not available yet" for M-Pesa. Payments already in flight are still confirmed.',
      danger: !enabled,
      reasonRequired: !enabled,
      okText: enabled ? 'Enable' : 'Disable',
    });
    if (reason === null) return;
    setToggling(true);
    try {
      setStatus(await api.setCollectionEnabled(enabled, reason || undefined));
      message.success(enabled ? 'Direct M-Pesa enabled.' : 'Direct M-Pesa disabled.');
      load();
    } catch (e) {
      message.error(api.apiErrorMessage(e, 'Could not change the collection state.'));
    } finally {
      setToggling(false);
    }
  };

  return (
    <div>
      {dialog}
      <PageHeader
        title="Payment collection"
        subtitle="Direct Vodacom M-Pesa into KaziPeople's own business account (subscription invoices)."
        actions={<Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>}
      />

      {loadFailed && <Alert type="error" showIcon className="mb-4" message="Status unavailable" description={loadFailed} />}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title={<Space><SafetyCertificateOutlined />M-Pesa (direct)</Space>} loading={loading && !status}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Alert type={head.tone === 'success' ? 'success' : head.tone === 'error' ? 'error' : head.tone === 'warning' ? 'warning' : 'info'} showIcon message={head.label} description={head.detail} />
              {status && (
                <Descriptions size="small" column={1} bordered>
                  <Descriptions.Item label="Environment">
                    {status.environment ? <Tag color={status.environment === 'live' ? 'red' : 'blue'}>{status.environment}</Tag> : <Text type="secondary">—</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Shortcode">{status.service_provider_code ?? <Text type="secondary">—</Text>}</Descriptions.Item>
                  <Descriptions.Item label="Credential version">
                    {status.active_version != null ? `v${status.active_version}` : '—'}{' '}
                    <Text type="secondary">({status.source === 'env' ? 'environment fallback' : status.source === 'db' ? 'saved here' : 'none'})</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="API key">
                    {status.api_key_set ? <Tag color="green">set · {status.api_key_fingerprint}</Tag> : <Tag>not set</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Public key">{status.public_key_valid ? <Tag color="green">valid</Tag> : <Tag color="orange">missing / invalid</Tag>}</Descriptions.Item>
                  <Descriptions.Item label="Enabled">{status.enabled ? <Tag color="green">yes</Tag> : <Tag>no</Tag>}</Descriptions.Item>
                  <Descriptions.Item label="Last test">
                    {status.last_test ? (
                      <Space direction="vertical" size={0}>
                        <span>
                          {status.last_test.ok ? <Tag color="green">connected</Tag> : <Tag color="red">failed</Tag>}
                          {fmtDateTime(status.last_test.at)} · v{status.last_test.version ?? '—'}
                        </span>
                        {status.last_test.message && <Text type="secondary">{status.last_test.message}</Text>}
                      </Space>
                    ) : (
                      <Text type="secondary">Never tested</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Pay-now methods">
                    M-Pesa via <Tag>{status.wallet_provider}</Tag> · Bank via <Tag>{status.bank_provider}</Tag>
                  </Descriptions.Item>
                  {!status.kek_configured && (
                    <Descriptions.Item label="Encryption">
                      <Tag color="red">BILLING_CONFIG_KEK not set — credentials cannot be saved</Tag>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              )}
              <Space wrap>
                <Button icon={<ApiOutlined />} onClick={runTest} loading={testing} disabled={!status}>Test connection</Button>
                {status?.enabled ? (
                  <Button danger onClick={() => toggle(false)} loading={toggling}>Disable</Button>
                ) : (
                  <Button type="primary" onClick={() => toggle(true)} loading={toggling} disabled={!status}>Enable</Button>
                )}
              </Space>
              <Text type="secondary" className="text-xs">Test connection only opens an M-Pesa session — it never charges anyone.</Text>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Save or rotate credentials">
            <Form form={form} layout="vertical" initialValues={{ environment: 'sandbox' }}>
              <Form.Item name="environment" label="Environment" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'sandbox', label: 'Sandbox (test)' },
                    { value: 'live', label: status?.live_allowed ? 'Live' : 'Live (production deployment only)', disabled: !status?.live_allowed },
                  ]}
                />
              </Form.Item>
              <Form.Item name="apiKey" label="API key" extra={status?.api_key_set ? 'Leave blank to keep the current key (same environment only). Never shown again after saving.' : 'Write-only — never shown again after saving.'}>
                <Input.Password autoComplete="new-password" placeholder={status?.api_key_set ? '•••••••• (unchanged)' : ''} />
              </Form.Item>
              <Form.Item name="publicKey" label="Public key" extra="The base64 public key from the M-Pesa OpenAPI portal. Leave blank to keep the current one.">
                <Input.TextArea rows={3} placeholder={status?.public_key_valid ? '(unchanged)' : ''} />
              </Form.Item>
              <Form.Item name="serviceProviderCode" label="Service provider code (shortcode)" rules={[{ pattern: /^\d{5,12}$/, message: '5–12 digits' }]} extra="Sandbox: 000000.">
                <Input inputMode="numeric" />
              </Form.Item>
              <Form.Item name="itemsDesc" label="Purchased items description (optional)">
                <Input maxLength={256} placeholder="KaziPeople subscription" />
              </Form.Item>
              <Button type="primary" onClick={save} loading={saving} disabled={!status?.kek_configured}>Save credentials</Button>
              <div className="mt-2"><Text type="secondary" className="text-xs">Saving creates a new version and disables collection until a passing test + enable.</Text></div>
            </Form>
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="Audit">
            {auditFailed ? (
              <Alert type="warning" showIcon message="Audit unavailable" description={auditFailed} />
            ) : audit.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No collection-config actions recorded yet" />
            ) : (
              <Table
                size="small"
                rowKey="id"
                pagination={{ pageSize: 20 }}
                dataSource={audit}
                columns={[
                  { title: 'When', dataIndex: 'createdAt', render: (v: string) => fmtDateTime(v), width: 170 },
                  { title: 'Action', dataIndex: 'action', render: (a: string) => <Tag>{auditLabel(a)}</Tag> },
                  { title: 'By', render: (_: unknown, r: api.CollectionAuditRow) => r.actorEmail || r.actorId || '—' },
                  {
                    title: 'Details',
                    dataIndex: 'details',
                    render: (d: Record<string, unknown>) => (
                      <Text type="secondary" className="text-xs">
                        {Object.entries(d || {})
                          .filter(([, v]) => v !== null && v !== undefined && v !== '')
                          .map(([k, v]) => `${k}: ${String(v)}`)
                          .join(' · ')}
                      </Text>
                    ),
                  },
                ]}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PaymentCollectionPage;
